<template>
  <div class="modal fade" id="sliderFormModal" tabindex="-1" aria-labelledby="sliderFormModalLabel" aria-hidden="true">
    <div class="modal-dialog custom-modal-dialog modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header bg-white border-bottom-0 pb-0">
          <div>
            <h5 class="modal-title fw-bold text-primary mb-1" id="sliderFormModalLabel">
              <i :class="isEditing ? 'bi bi-pencil-square' : 'bi bi-plus-circle'" class="me-2"></i>
              {{ isEditing ? 'Editar Slider' : 'Nuevo Slider' }}
            </h5>
            <p class="text-muted small mb-0">Configure el carrousel de imágenes principal.</p>
          </div>
          <button type="button" class="btn-close" @click="closeForm" aria-label="Close"></button>
        </div>

        <div class="modal-body pt-4">
          <form @submit.prevent="submitForm">

            <div class="fade-in">
              <h6 class="text-muted border-bottom pb-2 mb-4 small fw-bold text-uppercase">
                Contenido y Enlace
              </h6>

              <div class="mb-2">
                <ToggleField label="Título Principal" id="title" v-model="form.title" type="input" />
              </div>

              <div class="mb-2">
                <ToggleField label="Subtítulo / Descripción" id="content" v-model="form.content" type="textarea" />
              </div>

              <div class="mb-4">
                <ToggleField label="Enlace de Redirección" id="link" v-model="form.link" type="input" />
              </div>

              <div class="row g-3 mb-4">
                <div class="col-md-6">
                  <div class="form-floating">
                    <input type="text" v-model="form.button_text" class="form-control" id="button_text"
                      placeholder="Ej: Ver más" />
                    <label for="button_text">Texto Botón (Opcional)</label>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-floating">
                    <input type="number" v-model="form.order" class="form-control" id="order" placeholder="0" required>
                    <label for="order">Orden de Visualización</label>
                  </div>
                </div>
              </div>

              <h6 class="text-muted border-bottom pb-2 mb-3 small fw-bold text-uppercase">Multimedia</h6>
              <div class="row">
                <div class="col-12">
                  <FileUploadPreview accept="image/*,video/webm" label="Imagen o Video de Fondo" id="image"
                    :file="form.image" :preview="form.imagePreview" :required="!isEditing"
                    @file-change="updateFile('image', $event)" no-file-message="Arrastre una imagen/video aquí"
                    class="clean-upload" />
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4 pt-3 border-top">
                <button type="submit" class="btn btn-primary px-5 rounded-pill shadow-sm">
                  <i class="bi bi-check-lg me-2"></i>
                  {{ isEditing ? 'Guardar Cambios' : 'Crear Slider' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import { Modal } from 'bootstrap';
import { createSlider, updateSlider } from '@/services/api/sliderService.js';
import FileUploadPreview from '@/components/shared/FileUploadPreview.vue';
import ToggleField from '@/components/shared/ToggleField.vue';
import userConfirmation from "@/utils/userConfirmation.js";
import { showLoadingAlert, closeLoadingAlert } from "@/utils/loadingAlert.js";

const SLIDER_FORM_MODAL_ID = 'sliderFormModal';

export default {
  components: {
    FileUploadPreview,
    ToggleField,
  },
  props: {
    view: {
      type: Object,
      required: true,
    },
    header: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const isEditing = ref(false);
    const form = ref({
      title: '',
      content: '',
      link: '',
      button_text: '',
      image: null,
      order: '',
      view_id: props.view.id,
      imagePreview: '',
    });

    watch(() => props.header, (newHeader) => {
      if (newHeader) {
        isEditing.value = true;
        form.value = {
          title: newHeader.title,
          content: newHeader.content,
          link: newHeader.link,
          button_text: newHeader.button_text || '',
          image: null,
          order: newHeader.order,
          view_id: newHeader.view.id,
          imagePreview: newHeader.image,
        };
      } else {
        isEditing.value = false;
        form.value = {
          title: '',
          content: '',
          link: '',
          button_text: '',
          image: null,
          order: '',
          view_id: props.view.id,
          imagePreview: '',
        };
      }
    }, { immediate: true });

    const updateFile = (type, file) => {
      form.value[type] = file;
    };

    const submitForm = async () => {
      const formData = new FormData();
      Object.keys(form.value).forEach((key) => {
        if (key !== 'imagePreview' && (key !== 'image' || form.value.image)) {
          const val = form.value[key];
          if (val !== null && val !== undefined) {
            formData.append(key, val);
          }
        }
      });

      const editOrCreate = isEditing.value ? "editar" : "crear";
      const confirmed = await userConfirmation(`¿Está seguro de ${editOrCreate} el carrousel?`);

      if (!confirmed) return;

      showLoadingAlert();
      try {
        if (isEditing.value) {
          await updateSlider(props.header.id, formData);
          emit('success', 'Slider actualizado exitosamente.');
        } else {
          await createSlider(formData);
          emit('success', 'Slider creado exitosamente.');
        }

        emit('slider-created');
        closeForm();
      } catch (error) {
        emit('error', error.response?.data?.message || 'Error al procesar la solicitud.');
      } finally {
        closeLoadingAlert();
      }
    };

    const closeForm = () => {
      emit('close');
      const modalElement = document.getElementById(SLIDER_FORM_MODAL_ID);
      const modalInstance = Modal.getInstance(modalElement);
      if (modalInstance) modalInstance.hide();
    };

    onMounted(() => {
      const modalElement = document.getElementById(SLIDER_FORM_MODAL_ID);
      const modalInstance = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      });
      modalInstance.show();
    });

    return {
      form,
      isEditing,
      updateFile,
      submitForm,
      closeForm,
    };
  },
};
</script>

<style scoped>
.custom-modal-dialog {
  max-width: 700px;
  margin: 1.75rem auto;
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

::v-deep .clean-upload .border {
  background-color: #f8f9fa !important;
  border: 2px dashed #dee2e6 !important;
  color: #6c757d !important;
  transition: all 0.3s;
}

::v-deep .clean-upload .border:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
}

@media (max-width: 768px) {
  .custom-modal-dialog {
    max-width: 95%;
    margin: 10px auto;
  }
}
</style>